import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { Post, PostSchema } from "../../post/postSchema";
import { ScoreQuery } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { Campaign, CampaignSchema } from "src/api/client-api/useCampaign.ts";

export const getOneActionPlanKey = "getOneActionPlan" as const;

type Options = {
    actionPlanId: (string | number) | undefined;
    scoreQuery?: ScoreQuery;
    forceEnable?: boolean;
};
export const useGetOneActionPlan = (options: Options) => {
    const { actionPlanId, scoreQuery = {}, forceEnable = false } = options;

    const scoreQueryValues = Object.values(scoreQuery);
    const query = useQuery({
        enabled: actionPlanId !== undefined || forceEnable,
        queryKey: [getOneActionPlanKey, actionPlanId, ...scoreQueryValues],
        queryFn: async () => {
            const url = `/api/action_plans/${actionPlanId}`;

            const response = await loggedInApiCall.get(url, {
                params: scoreQuery,
            });
            return response;
        },
        select: (resp) => {
            const data = resp.data;
            const validatedData = zodParseWithLog(data, GetOneActionPlanSchema, getOneActionPlanKey);
            return validatedData;
        },
    });

    return query;
};

type ShortPostWithElements = Pick<Post, "id" | "scores" | "elements" | "campaign">;

export type OneActionPlan = ActionPlan & {
    simulationSsqPosts: ShortPostWithElements[];
};

export type SsqPost = Pick<Post, "id" | "campaign" | "scores" | "elements">;
type SsqPostWithCampaignOnly = Pick<Post, "id" | "campaign">;

const SsqPostSchema: z.ZodType<SsqPost> = PostSchema.pick({
    id: true,
    campaign: true,
    scores: true,
    elements: true,
});

const SsqPostWithCampaignOnlySchema: z.ZodType<SsqPostWithCampaignOnly> = PostSchema.pick({
    id: true,
    campaign: true,
});

type ActionPlanActions = {
    id: string;
    actionId: number;
    startSimulationSsqPost: SsqPostWithCampaignOnly;
    endSimulationSsqPost: SsqPostWithCampaignOnly;
};
const ActionPlanActionsSchema: z.ZodType<ActionPlanActions> = z.object({
    id: z.string(),
    actionId: z.number(),
    startSimulationSsqPost: SsqPostWithCampaignOnlySchema,
    endSimulationSsqPost: SsqPostWithCampaignOnlySchema,
});

type ActionPlanSsqPosts = {
    id: string;
    ssqPost: SsqPost;
};

const ActionPlanSsqPostsSchema: z.ZodType<ActionPlanSsqPosts> = z.object({
    id: z.string(),
    ssqPost: SsqPostSchema,
});

export type ActionPlanScoreTargets = {
    "@id": string;
    id: string;
    scoreId: number;
    variationId?: number;
    simulationVaryPercentage?: number;
    simulationVaryAbsolute?: number;
};
const ActionPlanScoreTargetsSchema: z.ZodType<ActionPlanScoreTargets> = z.object({
    "@id": z.string(),
    id: z.string(),
    scoreId: z.number(),
    variationId: z.number().optional(),
    simulationVaryPercentage: z.number().optional(),
    simulationVaryAbsolute: z.number().optional(),
});

export type ActionPlan = {
    "@id": IRI;
    id: string;
    name: string;
    description?: string;
    simulationVaryPercentage?: number;
    actionPlanSsqPosts: ActionPlanSsqPosts[];
    actionPlanScoreTargets: ActionPlanScoreTargets[];

    startSsqPost: SsqPost;
    endCampaign: Campaign;

    actionPlanActions: ActionPlanActions[];
};
const ExtendableActionPlanSchema = z.object({
    "@id": z.string(),
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    simulationVaryPercentage: z.number().optional(),
    actionPlanSsqPosts: ActionPlanSsqPostsSchema.array(),
    actionPlanScoreTargets: ActionPlanScoreTargetsSchema.array(),

    startSsqPost: SsqPostSchema,
    endCampaign: CampaignSchema,

    actionPlanActions: ActionPlanActionsSchema.array(),
});

const GetOneActionPlanSchema: z.ZodType<OneActionPlan> = ExtendableActionPlanSchema.extend({
    simulationSsqPosts: PostSchema.pick({
        id: true,
        scores: true,
        elements: true,
        campaign: true,
    }).array(),
});
